/**
 * useCountdown — live ticking countdown to a fixed deadline.
 * Clamps at zero once the deadline passes.
 */
import * as React from 'react';

export interface Countdown {
  days: number;
  hours: number;
  mins: number;
  secs: number;
  /** Whole days remaining (ceil) — for "Only N days left" copy. */
  daysLeft: number;
  expired: boolean;
}

function compute(deadlineMs: number): Countdown {
  const diff = Math.max(0, deadlineMs - Date.now());
  const secsTotal = Math.floor(diff / 1000);
  return {
    days: Math.floor(secsTotal / 86_400),
    hours: Math.floor((secsTotal % 86_400) / 3_600),
    mins: Math.floor((secsTotal % 3_600) / 60),
    secs: secsTotal % 60,
    daysLeft: Math.ceil(diff / 86_400_000),
    expired: diff === 0,
  };
}

export function useCountdown(deadlineIso: string): Countdown {
  const deadlineMs = React.useMemo(() => new Date(deadlineIso).getTime(), [deadlineIso]);
  const [value, setValue] = React.useState<Countdown>(() => compute(deadlineMs));

  React.useEffect(() => {
    const id = window.setInterval(() => setValue(compute(deadlineMs)), 1000);
    return () => window.clearInterval(id);
  }, [deadlineMs]);

  return value;
}

/** Zero-pads to two digits: 7 -> "07". */
export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
